const post = {
  slug: 'shipping-ml-models-is-the-easy-part',
  number: 12,
  title: 'Training the Model Is the Easy Part',
  date: '2024-12-05',
  readTime: '7 min read',
  views: 28,
  category: 'ai',
  tags: ['AI/ML', 'MLOps', 'Production'],
  excerpt:
    'A model that scores well in a notebook is maybe a quarter of the work. The other three quarters is everything that keeps it useful after it ships, including the part where it quietly rots.',
  cover: null,
  content: `
There is a moment on every machine learning project where the model finally scores well on the test set and it feels like you are basically done. You are not. That moment is maybe a quarter of the work. The other three quarters is everything that keeps the model useful after it leaves the notebook, and it is the part that decides whether the project ever helps a real person.

When I shipped credit risk models into production at Stax, this landed hard. The accuracy was never the hard part. Keeping a live model honest, fed the right data, and watched closely enough to notice when it started to drift, was the whole job.

## Serving is a real engineering problem

A model in a notebook is a function you call by hand. A model in production has to answer requests reliably, quickly, and under load, which is a completely different set of constraints. It needs the same things any service needs, and treating it as exempt because it is "AI" is how you end up debugging a black box at the worst possible time.

- Latency budgets, because a prediction nobody waits for is useless.
- Timeouts and a fallback, so a slow model does not take the request down with it.
- Versioning, so you know exactly which model produced which decision months later.
- A way to roll back when a new version misbehaves, which it eventually will.

Treat the model as just another backend dependency, with all the discipline that implies, and it becomes dependable. Treat it as special, and it becomes a liability with good test-set numbers.

## The data at inference never matches the notebook

The subtle trap is that the data your model sees in production is rarely shaped exactly like your clean training data. A feature gets computed slightly differently in the live path. A value that was never null in training shows up null. A category appears that the model never saw.

The model does not error on any of this. It quietly produces a worse prediction, which is far harder to notice than a crash, because nothing looks broken. The defence is validation at the boundary. Check the inputs before they reach the model, the same way you would validate any request, and flag or reject the ones that do not look like what the model was trained on.

\`\`\`text
before predict():
  assert all required features present
  assert each feature within the range seen in training
  if not -> log, flag, and handle explicitly (don't silently predict)
\`\`\`

A loud rejection beats a silent wrong answer every single time.

## Models decay, and you have to watch for it

This is the part with no equivalent in normal software, and the part teams forget. A correct function stays correct forever. A model slowly goes stale as the world it learned from drifts away from the world it now runs in. Customer behaviour shifts, the economy moves, and a model that was accurate at launch is quietly less accurate six months later, with nothing in the code having changed.

So you monitor a model the way you monitor a service, but for different signals:

- The distribution of inputs over time, to catch the world shifting underneath it.
- The distribution of predictions, to catch the model drifting in response.
- Real outcomes against what it predicted, whenever you can eventually observe them.

When those start to diverge, that is your signal to retrain. Without this monitoring, you simply will not know the model has gone bad until someone downstream feels the consequences, and by then it has been wrong for a while.

## A shipping checklist

Before a model goes near production, I want all of these true:

- It is served like a real service: timeouts, versioning, rollback, a fallback.
- Inputs are validated at the boundary against the training distribution.
- Every prediction is logged with the model version that made it.
- Input and prediction distributions are monitored for drift.
- There is a plan, and ideally a trigger, for retraining when drift appears.

## The mindset that holds it together

Shipping a model is not the end of a data science project, it is the start of an operations problem. The model is a component that needs serving infrastructure, input validation, versioning, and ongoing monitoring, exactly like any other part of a production system, plus the extra wrinkle that it can rot in place while looking fine.

Get good at that unglamorous three quarters, and your models keep earning their keep long after the notebook that made them is forgotten. Skip it, and you have shipped a number that was true once.
`,
};

export default post;
